import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import Select from 'react-select';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function StockTransfer() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [itemOptions, setItemOptions] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemId, setItem] = useState(0);

    const [branchOptions, setBranchOptions] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branch, setBranch] = useState(0);

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const [OrionQty, setOrionQty] = useState(0);
    const [UbcityQty, setUbcityQty] = useState(0);
    const [PMCChennaiQty, setPMCChennaiQty] = useState(0);
    const [PMCBangaloreQty, setPMCBangaloreQty] = useState(0);
    const [HYDForumQty, setHYDForumQty] = useState(0);
    const [InorbitQty, setInorbitQty] = useState(0);
    const [LandTQty, setLandTQty] = useState(0);
    const [VRQty, setVRQty] = useState(0);
    const [ShantiniketanQty, setShantiniketanQty] = useState(0);
    const [HeadOfficeQty, setHeadOfficeQty] = useState(0);
    const [selectedOption, setSelectedOption] = useState('out');

    useEffect(() => {
        let OrionQty = 0, UbcityQty = 0, PMCChennaiQty = 0, PMCBangaloreQty = 0;
        let HYDForumQty = 0, InorbitQty = 0, LandTQty = 0, VRQty = 0, ShantiniketanQty = 0, HeadOfficeQty = 0;
        dataItem.forEach(item => {
            OrionQty += item.OrionQty; UbcityQty += item.UbcityQty; PMCChennaiQty += item.PMCChennaiQty;
            PMCBangaloreQty += item.PMCBangaloreQty; HYDForumQty += item.HYDForumQty; InorbitQty += item.InorbitQty;
            LandTQty += item.LandTQty; VRQty += item.VRQty; ShantiniketanQty += item.ShantiniketanQty; HeadOfficeQty += item.HeadOfficeQty;
        });
        setOrionQty(OrionQty); setUbcityQty(UbcityQty); setPMCChennaiQty(PMCChennaiQty);
        setPMCBangaloreQty(PMCBangaloreQty); setHYDForumQty(HYDForumQty); setInorbitQty(InorbitQty);
        setLandTQty(LandTQty); setVRQty(VRQty); setShantiniketanQty(ShantiniketanQty); setHeadOfficeQty(HeadOfficeQty);
    }, [dataItem]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
        fetchBranchData();
        fetchItemData();
    }, [isAuthenticated, companyId, token, logout]);

    const handleBranchSelect = (selectedOption) => {
        setSelectedBranch(selectedOption);
        setBranch(selectedOption ? selectedOption.value : '');
        setDataItem([]);
    };

    const handleItemSelect = (selectedOption) => {
        setSelectedItem(selectedOption);
        setItem(selectedOption ? selectedOption.value : 0);
        setDataItem([]);
    };

    const fetchBranchData = () => {
        fetchData('ReportApi', `/Branch/${companyId}/${branchId}`, token)
            .then((response) => response.response_data)
            .then((data) => setBranchOptions(data.map((c) => ({ label: c.Name, value: c.Id }))))
            .catch((error) => console.error('Error fetching branch data:', error));
    };

    const fetchItemData = () => {
        fetchData('ReportApi', `/Item/${companyId}/${branchId}`, token)
            .then((response) => response.response_data)
            .then((data) => setItemOptions(data.map((c) => ({ label: c.Code + '-' + c.Name, value: c.Id }))))
            .catch((error) => console.error('Error fetching item data:', error));
    };

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
    const handleFromDateChange = (e) => setFromDate(e.target.value);
    const handleToDateChange = (e) => setToDate(e.target.value);
    const handleChange = (event) => { setSelectedOption(event.target.value); setDataItem([]); };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const endpoint = selectedOption === 'out'
                    ? `/StockTransferout/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId}`
                    : `/StockTransferint/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId}`;
                const response = await fetchData('ReportApi', endpoint, token);
                if (response.responseCode === '1111') setDataItem(response.response_data);
            } catch (error) {
                console.error('Error fetching Stock Transfer Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) => String(value).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' });

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Stock');
        const headerCaption = selectedOption === 'out'
            ? `Stock Transfer Out From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`
            : `Stock Transfer In From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:V1');
        const captionCell = worksheet.getCell('A1');
        captionCell.value = headerCaption;
        captionCell.font = { bold: true, size: 14 };
        captionCell.alignment = { vertical: 'middle', horizontal: 'left' };
        const headerRowValues = ['Code', 'Item', 'Purchase Rate', 'MRP', 'GST%', 'HSN Code', 'PG', 'IG', 'ISG', 'PF', 'Category', 'Group', 'UB City', 'Orion Mall', 'PMC Chennai', 'PMC Bangalore', 'Inorbit Mall', 'L&T Metro Mall', 'VR Mall', 'Hyderabad Forum', 'Shantiniketan', 'Ecomm'];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));
        const rightAlignColumns = ['Purchase Rate', 'MRP', 'GST%', 'UB City', 'Orion Mall', 'PMC Chennai', 'PMC Bangalore', 'Inorbit Mall', 'L&T Metro Mall', 'VR Mall', 'Hyderabad Forum', 'Shantiniketan', 'Ecomm'];
        filteredDataItem.forEach((rowData) => {
            const row = worksheet.addRow([parseFloat(rowData.Code), rowData.Name, parseFloat(rowData.PurchaseRate.toFixed(2)), parseFloat(rowData.MRP.toFixed(2)), parseFloat(rowData.GSTRate), rowData.HSNCode, rowData.PG, rowData.IG, rowData.ISG, rowData.PF, rowData.CategoryName, rowData.ItemGroupName, parseFloat(rowData.UbcityQty), parseFloat(rowData.OrionQty), parseFloat(rowData.PMCChennaiQty), parseFloat(rowData.PMCBangaloreQty), parseFloat(rowData.InorbitQty), parseFloat(rowData.LandTQty), parseFloat(rowData.VRQty), parseFloat(rowData.HYDForumQty), parseFloat(rowData.ShantiniketanQty), parseFloat(rowData.HeadOfficeQty)]);
            row.eachCell((cell, colNumber) => {
                if (rightAlignColumns.includes(headerRowValues[colNumber - 1])) cell.alignment = { vertical: 'middle', horizontal: 'right' };
                else cell.alignment = { vertical: 'middle', horizontal: 'center' };
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        const totalRow = worksheet.addRow(['', 'Total', '', '', '', '', '', '', '', '', '', '', UbcityQty, OrionQty, PMCChennaiQty, PMCBangaloreQty, InorbitQty, LandTQty, VRQty, HYDForumQty, ShantiniketanQty, HeadOfficeQty]);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            if (rightAlignColumns.includes(headerRowValues[colNumber - 1])) cell.alignment = { vertical: 'middle', horizontal: 'right' };
            else cell.alignment = { vertical: 'middle', horizontal: 'center' };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Stock_Transfer.xlsx');
    };

    const itemOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...itemOptions];
    const branchOptionsWithBlank = [{ label: '--Select--', value: 0 }, ...branchOptions];

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Stock Transfer Report</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={handleFromDateChange} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={handleToDateChange} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row" style={{ alignItems: 'center' }}>
                        <RadioGroup row value={selectedOption} onChange={handleChange} name="viewOption">
                            <FormControlLabel value="out" control={<Radio />} label="Out" />
                            <FormControlLabel value="in" control={<Radio />} label="In" />
                        </RadioGroup>
                    </div>
                    <div className="pg-action-row">
                        <div className="pg-field" style={{ minWidth: 180 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Item</label>
                            <Select options={itemOptionsWithBlank} value={selectedItem} onChange={handleItemSelect} placeholder="--Select--"
                                styles={{ control: (base) => ({ ...base, minHeight: 31, height: 31, fontSize: 12.5 }) }} />
                        </div>
                        <div className="pg-field" style={{ minWidth: 180 }}>
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>Branch</label>
                            <Select options={branchOptionsWithBlank} value={selectedBranch} onChange={handleBranchSelect} placeholder="--Select--"
                                styles={{ control: (base) => ({ ...base, minHeight: 31, height: 31, fontSize: 12.5 }) }} />
                        </div>
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}><FeedIcon /> Excel</button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearchInput(v => !v)}><SearchIcon /> Search</button>
                    </div>
                    {showSearchInput && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Item</th>
                            <th style={{ textAlign: 'right' }}>Purchase Rate</th>
                            <th style={{ textAlign: 'right' }}>MRP</th>
                            <th style={{ textAlign: 'right' }}>GST%</th>
                            <th>HSN Code</th>
                            <th>PG</th>
                            <th>IG</th>
                            <th>ISG</th>
                            <th>PF</th>
                            <th>Category</th>
                            <th>Group</th>
                            <th style={{ textAlign: 'right' }}>UB City</th>
                            <th style={{ textAlign: 'right' }}>Orion Mall</th>
                            <th style={{ textAlign: 'right' }}>PMC Chennai</th>
                            <th style={{ textAlign: 'right' }}>PMC Bangalore</th>
                            <th style={{ textAlign: 'right' }}>Inorbit Mall</th>
                            <th style={{ textAlign: 'right' }}>L&amp;T Metro Mall</th>
                            <th style={{ textAlign: 'right' }}>VR Mall</th>
                            <th style={{ textAlign: 'right' }}>Hyderabad Forum</th>
                            <th style={{ textAlign: 'right' }}>Shantiniketan</th>
                            <th style={{ textAlign: 'right' }}>Ecomm</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={22} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filteredDataItem.map((data, index) => (
                                <tr key={index}>
                                    <td>{data.Code}</td>
                                    <td>{data.Name}</td>
                                    <td style={{ textAlign: 'right' }}>{data.PurchaseRate.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{data.MRP.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{data.GSTRate}</td>
                                    <td>{data.HSNCode}</td>
                                    <td>{data.PG}</td>
                                    <td>{data.IG}</td>
                                    <td>{data.ISG}</td>
                                    <td>{data.PF}</td>
                                    <td>{data.CategoryName}</td>
                                    <td>{data.ItemGroupName}</td>
                                    <td style={{ textAlign: 'right' }}>{data.UbcityQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.OrionQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.PMCChennaiQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.PMCBangaloreQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.InorbitQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.LandTQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.VRQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.HYDForumQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.ShantiniketanQty}</td>
                                    <td style={{ textAlign: 'right' }}>{data.HeadOfficeQty}</td>
                                </tr>
                            ))
                        }
                        <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                            <td></td>
                            <td style={{ padding: '6px 10px' }}>Total</td>
                            <td colSpan={10}></td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{UbcityQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{OrionQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{PMCChennaiQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{PMCBangaloreQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{InorbitQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{LandTQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{VRQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{HYDForumQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{ShantiniketanQty}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{HeadOfficeQty}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default StockTransfer;
